// Lightweight SVG sparkline / area chart for time series data.
// Props:
//   data: [{date: 'YYYY-MM-DD', annonces: n, users: n}]  (30 entries)
//   metric: 'annonces' | 'users'
//   color: tailwind hex (default orange)
import { useMemo, useState } from "react";

export default function Sparkline({ data = [], metric = "annonces", color = "#f97316", testid }) {
  const [hover, setHover] = useState(null);

  const points = useMemo(() => data.map((d) => d[metric] || 0), [data, metric]);
  const max = Math.max(1, ...points);
  const total = points.reduce((a, b) => a + b, 0);

  const W = 600;
  const H = 120;
  const PAD_X = 8;
  const PAD_Y = 12;

  if (!points.length) return null;

  const stepX = (W - PAD_X * 2) / Math.max(1, points.length - 1);
  const coords = points.map((v, i) => {
    const x = PAD_X + i * stepX;
    const y = H - PAD_Y - ((v / max) * (H - PAD_Y * 2));
    return { x, y, v, date: data[i].date };
  });

  const line = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
  const area = `${line} L ${coords[coords.length - 1].x.toFixed(1)} ${(H - PAD_Y).toFixed(1)} L ${coords[0].x.toFixed(1)} ${(H - PAD_Y).toFixed(1)} Z`;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = W / rect.width;
    const x = (e.clientX - rect.left) * ratio;
    const i = Math.min(points.length - 1, Math.max(0, Math.round((x - PAD_X) / stepX)));
    setHover({ i, ...coords[i] });
  };

  const onLeave = () => setHover(null);
  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  };

  return (
    <div className="relative" data-testid={testid}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-32 cursor-crosshair"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        role="img"
      >
        <defs>
          <linearGradient id={`sparkfill-${metric}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.32" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* horizontal gridlines */}
        {[0.25, 0.5, 0.75].map((p) => (
          <line
            key={p}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={H - PAD_Y - p * (H - PAD_Y * 2)}
            y2={H - PAD_Y - p * (H - PAD_Y * 2)}
            stroke="rgba(0,0,0,0.06)"
            strokeDasharray="3 3"
          />
        ))}
        <path d={area} fill={`url(#sparkfill-${metric})`} />
        <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Data points */}
        {coords.map((c, i) => (
          <circle
            key={c.date}
            cx={c.x}
            cy={c.y}
            r={hover?.i === i ? 4 : 0}
            fill={color}
            stroke="#fff"
            strokeWidth="2"
          />
        ))}
        {/* Hover guide line */}
        {hover && (
          <line x1={hover.x} x2={hover.x} y1={PAD_Y} y2={H - PAD_Y} stroke={color} strokeDasharray="2 3" strokeOpacity="0.6" />
        )}
      </svg>
      {/* Axes labels */}
      <div className="flex justify-between text-[10px] text-[var(--ink-soft)] font-semibold mt-1 px-1">
        <span>{fmtDate(data[0].date)}</span>
        <span className="hidden sm:inline">{fmtDate(data[Math.floor(data.length / 2)].date)}</span>
        <span>{fmtDate(data[data.length - 1].date)}</span>
      </div>
      {/* Tooltip */}
      {hover && (
        <div
          className="pointer-events-none absolute -top-2 -translate-y-full bg-[var(--ink)] text-white text-xs px-2 py-1 rounded-lg shadow-lg whitespace-nowrap"
          style={{ left: `${(hover.x / W) * 100}%`, transform: "translate(-50%, -100%)" }}
          data-testid={`${testid}-tooltip`}
        >
          <b>{hover.v}</b> {metric} - {fmtDate(hover.date)}
        </div>
      )}
      {/* Footer */}
      <div className="flex items-baseline justify-between mt-2 text-xs">
        <span className="text-[var(--ink-soft)]">Total 30j</span>
        <span className="font-bold" style={{ color }}>{total.toLocaleString("fr-FR")}</span>
      </div>
    </div>
  );
}
